import React, { useEffect, useRef, useState } from 'react';
import TopBar from 'components/TopBar/TopBar';
import Copyright from 'components/Copyright/Copyright';
import {
	Wrapper,
	Intro,
	IntroEyebrow,
	IntroTitle,
	IntroLead,
	IntroMeta,
	Layout,
	TocNav,
	TocLabel,
	TocList,
	TocItem,
	TocLink,
	TocNum,
	Main,
	Article,
	ArticleHeading,
	ArticleNum,
	SubHeading,
	P,
	List,
	OrderedList,
	Callout,
	TableWrap,
	Table,
	Th,
	Td,
	Address,
	BackToTop,
} from 'components/LegalDocument/styled';

const SECTIONS = [
	{ id: 'who-we-are', num: '1', title: 'Who We Are' },
	{ id: 'information-we-collect', num: '2', title: 'Information We Collect' },
	{ id: 'nationality', num: '3', title: 'Nationality and Nationality Groups' },
	{ id: 'how-we-use', num: '4', title: 'How We Use Your Information' },
	{ id: 'legal-bases', num: '5', title: 'Legal Bases for Processing' },
	{ id: 'how-we-share', num: '6', title: 'How We Share Your Information' },
	{ id: 'data-retention', num: '7', title: 'Data Retention' },
	{ id: 'your-rights', num: '8', title: 'Your Rights and Choices' },
	{ id: 'data-security', num: '9', title: 'Data Security' },
	{ id: 'childrens-privacy', num: '10', title: "Children's Privacy" },
	{ id: 'international-transfers', num: '11', title: 'International Data Transfers' },
	{ id: 'third-party-links', num: '12', title: 'Third-Party Links and Institutional Systems' },
	{ id: 'changes', num: '13', title: 'Changes to This Policy' },
	{ id: 'contact', num: '14', title: 'Contact Us' },
];

const Privacy = () => {
	const [activeId, setActiveId] = useState(SECTIONS[0].id);
	const sectionRefs = useRef({});

	useEffect(() => {
		document.title = 'Privacy Policy | Flass';
	}, []);

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) setActiveId(entry.target.id);
				});
			},
			{ rootMargin: '-15% 0px -70% 0px', threshold: 0 },
		);
		SECTIONS.forEach(({ id }) => {
			const el = sectionRefs.current[id];
			if (el) observer.observe(el);
		});
		return () => observer.disconnect();
	}, []);

	return (
		<Wrapper id="top">
			<TopBar />

			<Intro>
				<IntroEyebrow>Legal</IntroEyebrow>
				<IntroTitle>Privacy Policy</IntroTitle>
				<IntroLead>
					How Flass collects, uses, and protects information for
					students, lecturers, and staff using the Flass app and
					its participating-institution communities.
				</IntroLead>
				<IntroMeta>
					<span>Effective August 8, 2026</span>
					<span>Updated August 8, 2026</span>
				</IntroMeta>
			</Intro>

			<Layout>
				<TocNav aria-label="Privacy policy sections">
					<TocLabel>Contents</TocLabel>
					<TocList>
						{SECTIONS.map((section) => (
							<TocItem key={section.id}>
								<TocLink
									href={`#${section.id}`}
									$active={activeId === section.id}
								>
									<TocNum>{section.num}</TocNum>
									{section.title}
								</TocLink>
							</TocItem>
						))}
					</TocList>
				</TocNav>

				<Main>
					<P>
						This Privacy Policy explains how Ergi Laze, the
						individual developer of Flass (&ldquo;
						<strong>we</strong>,&rdquo; &ldquo;<strong>us</strong>
						,&rdquo; or &ldquo;<strong>our</strong>&rdquo;),
						collects, uses, discloses, and safeguards information
						when you use the Flass mobile application, website,
						and related services (collectively, the &ldquo;
						<strong>Service</strong>&rdquo;). It applies to
						students, lecturers, staff, and administrators who
						access the Service through a participating
						educational institution (&ldquo;
						<strong>Institution</strong>&rdquo;) or by creating an
						account directly.
					</P>
					<P>
						By using the Service, you agree to the collection and
						use of information in accordance with this Policy. If
						you do not agree, please do not use the Service.
					</P>

					<Article
						id="who-we-are"
						ref={(el) => (sectionRefs.current['who-we-are'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>1</ArticleNum>Who We Are
						</ArticleHeading>
						<P>
							Flass is developed and operated by Ergi Laze, an
							individual developer based in Albania, rather
							than a registered company.
						</P>
						<P>
							For the purposes of applicable data protection law
							(including the EU/UK General Data Protection
							Regulation, &ldquo;<strong>GDPR</strong>&rdquo;),
							Ergi Laze acts as a data controller with respect
							to account and usage information described in
							this Policy, and in certain respects as a data
							processor on behalf of an Institution with
							respect to enrollment and roster data that the
							Institution provides to us. If you have questions
							about how your Institution shares information
							with us, please also refer to your
							Institution&rsquo;s own privacy notices.
						</P>
						<Callout>
							<strong>Contact</strong> — ergilaze@gmail.com
							<br />
							<strong>Data Protection Officer</strong> — not
							currently designated; this threshold generally
							does not apply to an individual developer without
							an EU/UK establishment or large-scale processing
							under GDPR Article&nbsp;37. Revisit if that
							changes.
						</Callout>
					</Article>

					<Article
						id="information-we-collect"
						ref={(el) =>
							(sectionRefs.current['information-we-collect'] = el)
						}
					>
						<ArticleHeading>
							<ArticleNum>2</ArticleNum>Information We Collect
						</ArticleHeading>
						<P>We collect the following categories of information:</P>

						<SubHeading>2.1 Account and registration information</SubHeading>
						<List>
							<li>
								<strong>Institutional sign-in (Moodle/SSO)</strong> —
								if your Institution has integrated Flass with
								its learning management system, we receive
								your name, email address, institutional user
								ID, department/course information, and role
								(e.g., student, lecturer, teaching staff,
								administrator) from your Institution when you
								log in.
							</li>
							<li>
								<strong>Direct sign-in</strong> — if you
								register directly with an email address and
								password, we collect that email address and a
								securely hashed (never plaintext) version of
								your password.
							</li>
							<li>
								<strong>Profile information</strong> — name,
								profile photo, role, and any other information
								you choose to add to your profile.
							</li>
						</List>

						<SubHeading>2.2 User content</SubHeading>
						<P>Content you create or share through the Service, including:</P>
						<List>
							<li>
								Chat messages, replies, forwarded messages,
								and reactions, in group rooms,
								institution-wide/server rooms, and private
								one-to-one conversations;
							</li>
							<li>Photos, documents, and other files you upload or send;</li>
							<li>Polls, surveys, and their results;</li>
							<li>Events you create or RSVP to;</li>
							<li>
								Marketplace listings you post (title,
								description, price, category, condition,
								images, general location) and messages
								exchanged about a listing;
							</li>
							<li>
								Job and internship postings you create or
								apply to, and any contact information you
								include in them;
							</li>
							<li>
								Office-hours sessions, including your position
								in a queue and any notes a lecturer records
								about a session.
							</li>
						</List>
						<P>
							Content shared in a group, server, or private room
							is visible to the other members of that room, and
							to your Institution&rsquo;s authorized staff where
							applicable (e.g., moderation, safety, or
							academic-integrity purposes).
						</P>

						<SubHeading>2.3 Location information</SubHeading>
						<P>
							The Service includes an optional feature that lets
							you share your current location within a chat
							message. We only access your device&rsquo;s
							precise or approximate location if you explicitly
							enable this feature and choose to share it in a
							specific message. We do not track your location
							in the background or outside of this explicit
							sharing action.
						</P>

						<SubHeading>2.4 Camera, microphone, and media library</SubHeading>
						<P>
							With your permission, we access your
							device&rsquo;s camera, microphone, and photo
							library so you can:
						</P>
						<List>
							<li>Take or upload a profile photo;</li>
							<li>
								Attach photos, videos, or documents to a
								message, event, marketplace listing, or job
								posting;
							</li>
							<li>Record and send voice messages.</li>
						</List>
						<P>
							We only access these device capabilities when you
							actively use a related feature, and only after
							your device&rsquo;s operating system has granted
							the corresponding permission, which you can revoke
							at any time in your device settings.
						</P>

						<SubHeading>2.5 Device, push notification, and usage information</SubHeading>
						<List>
							<li>
								<strong>Push notification tokens</strong> — a
								device-specific token (via Apple Push
								Notification service, Firebase Cloud
								Messaging, or the Expo push service) used to
								deliver notifications about new messages,
								event reminders, office-hours updates, and
								similar activity.
							</li>
							<li>
								<strong>Device and app information</strong> —
								device model, operating system version, app
								version, and locale, used for compatibility,
								security, and troubleshooting.
							</li>
							<li>
								<strong>Log and diagnostic data</strong> — IP
								address, timestamps, and error/crash
								information generated by ordinary operation of
								the Service.
							</li>
							<li>
								<strong>Storage usage</strong> — the amount of
								file storage your account has used, to
								enforce per-account storage limits.
							</li>
						</List>

						<SubHeading>2.6 Information we do not intentionally collect</SubHeading>
						<P>
							We do not use the Service to collect precise
							background location, contact lists, browsing
							history outside the Service, or biometric
							identifiers, and we do not knowingly collect
							financial account or payment card information
							(the Service does not process in-app payments).
						</P>
					</Article>

					<Article
						id="nationality"
						ref={(el) => (sectionRefs.current['nationality'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>3</ArticleNum>Nationality and Nationality
							Groups
						</ArticleHeading>
						<P>
							Where your Institution has enabled this feature, Flass
							asks you to select up to two nationalities the first
							time you sign in. This section explains exactly what
							happens with that information, because it is the most
							sensitive category the Service collects.
						</P>

						<SubHeading>What we do with it</SubHeading>
						<P>
							Selecting a nationality adds you to a nationality
							group — a shared space for members of your Institution
							who selected the same one. The purpose is to help
							students, and international students in particular,
							find others from their country.
						</P>
						<Callout>
							<P>
								<strong>
									Other members of a nationality group can see
									that you are in it.
								</strong>{' '}
								Group membership is visible to the other people in
								that group, in the same way as any other group in
								Flass. Choosing a nationality therefore discloses
								it to other members of that group at your
								Institution. It is not shown publicly, is not
								visible outside your Institution, and is never
								used for advertising or shared with third parties.
							</P>
						</Callout>

						<SubHeading>Legal basis</SubHeading>
						<P>
							Your Institution is the data controller and determines
							the legal basis for this processing. Depending on how
							your Institution has configured the Service and on
							applicable law, information about nationality may be
							treated as revealing racial or ethnic origin, and so
							as a special category of personal data under Article 9
							of the UK and EU GDPR. Where that is the case, your
							Institution is responsible for identifying an
							appropriate Article 9 condition. Flass processes this
							information only on your Institution&apos;s documented
							instructions.
						</P>

						<SubHeading>Your choices</SubHeading>
						<List>
							<li>
								You may select one or two nationalities, or the
								same choice may be required before you continue,
								depending on your Institution&apos;s
								configuration.
							</li>
							<li>
								You can change your selection later. Changing it
								removes you from the nationality groups you no
								longer have selected and adds you to the ones you
								do.
							</li>
							<li>
								Your Institution can disable nationality
								collection entirely. Where it is disabled, you are
								not asked, and no nationality groups are created.
							</li>
							<li>
								If you would prefer not to provide this
								information, contact your Institution&apos;s
								administrator, who can advise on the options
								available to you.
							</li>
						</List>
					</Article>

					<Article
						id="how-we-use"
						ref={(el) => (sectionRefs.current['how-we-use'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>4</ArticleNum>How We Use Your Information
						</ArticleHeading>
						<P>We use the information described above to:</P>
						<OrderedList>
							<li>Create and maintain your account and authenticate you;</li>
							<li>
								Provide the core functionality of the Service
								— messaging, groups, events, marketplace, job
								postings, office hours, and notifications;
							</li>
							<li>
								Enable communication and content-sharing
								between you and other members of your
								Institution&rsquo;s community, consistent with
								your room memberships and privacy settings;
							</li>
							<li>
								Send you push notifications, email
								notifications (such as event reminders), and
								other service communications;
							</li>
							<li>
								Maintain the security and integrity of the
								Service, including detecting and preventing
								fraud, abuse, and violations of our terms;
							</li>
							<li>
								Enforce per-account storage limits and manage
								technical performance (e.g., caching of
								frequently accessed data);
							</li>
							<li>Respond to support requests and communicate with you about the Service;</li>
							<li>
								Comply with legal obligations and cooperate
								with your Institution&rsquo;s lawful requests
								where required by our agreement with that
								Institution;
							</li>
							<li>Improve and develop the Service.</li>
						</OrderedList>
						<P>
							We do not sell your personal information, and we
							do not use your content to serve third-party
							advertising.
						</P>
					</Article>

					<Article
						id="legal-bases"
						ref={(el) => (sectionRefs.current['legal-bases'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>5</ArticleNum>Legal Bases for Processing{' '}
							<span style={{ opacity: 0.5, fontWeight: 400, fontSize: '0.7em' }}>
								(EEA/UK users)
							</span>
						</ArticleHeading>
						<P>
							If you are located in the European Economic Area
							or the United Kingdom, we process your
							information under the following legal bases:
						</P>
						<List>
							<li>
								<strong>Performance of a contract</strong> — to
								provide the Service you have registered to
								use;
							</li>
							<li>
								<strong>Legitimate interests</strong> — to
								secure the Service, prevent abuse, and improve
								functionality, balanced against your rights;
							</li>
							<li>
								<strong>Consent</strong> — for optional
								features such as location sharing,
								camera/microphone access, and certain
								notifications, which you may withdraw at any
								time;
							</li>
							<li>
								<strong>Legal obligation</strong> — where we
								must retain or disclose information to comply
								with the law or a lawful request from your
								Institution.
							</li>
						</List>
					</Article>

					<Article
						id="how-we-share"
						ref={(el) => (sectionRefs.current['how-we-share'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>6</ArticleNum>How We Share Your Information
						</ArticleHeading>
						<P>We share information in the following circumstances:</P>

						<SubHeading>5.1 With other users</SubHeading>
						<P>
							Content you post in a group, server room, or
							private conversation is visible to the other
							members of that room. Your profile information
							(name, photo, role) is visible to other users of
							your Institution&rsquo;s Flass instance as
							reasonably necessary for the Service to function
							(e.g., appearing in a member list, a marketplace
							listing, or a job posting).
						</P>

						<SubHeading>5.2 With your Institution</SubHeading>
						<P>
							If you access the Service through an Institution,
							that Institution may receive usage information
							and, where required for academic-integrity,
							safety, or moderation purposes, may be able to
							access content within its own instance of the
							Service, consistent with our agreement with that
							Institution and its own policies.
						</P>

						<SubHeading>5.3 With service providers (subprocessors)</SubHeading>
						<P>
							We share information with third-party vendors who
							perform services on our behalf, under contractual
							confidentiality and data-protection obligations,
							including:
						</P>
						<TableWrap>
							<Table>
								<thead>
									<tr>
										<Th>Category</Th>
										<Th>Provider</Th>
										<Th>Purpose</Th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<Td>Cloud database hosting</Td>
										<Td>MongoDB Atlas (EU region)</Td>
										<Td>Storing account, room, and message data</Td>
									</tr>
									<tr>
										<Td>Cloud file storage</Td>
										<Td>Wasabi Technologies (Netherlands)</Td>
										<Td>Storing uploaded photos, documents, and other files</Td>
									</tr>
									<tr>
										<Td>Backend hosting</Td>
										<Td>Railway</Td>
										<Td>Hosting the application backend</Td>
									</tr>
									<tr>
										<Td>Caching and background jobs</Td>
										<Td>Redis Cloud</Td>
										<Td>
											Improving performance of frequently
											accessed data; processing
											background notification jobs
										</Td>
									</tr>
									<tr>
										<Td>Email delivery</Td>
										<Td>Resend</Td>
										<Td>Sending event reminders and account-related email</Td>
									</tr>
									<tr>
										<Td>Error monitoring</Td>
										<Td>Sentry</Td>
										<Td>Diagnosing and fixing backend errors</Td>
									</tr>
									<tr>
										<Td>Push notification delivery</Td>
										<Td>Apple (APNs), Google (FCM), Expo</Td>
										<Td>Delivering mobile push notifications</Td>
									</tr>
								</tbody>
							</Table>
						</TableWrap>
						<P>
							We require these providers to use the information
							we share with them only to provide services to us
							and in a manner consistent with this Policy.
						</P>

						<SubHeading>5.4 Legal and safety reasons</SubHeading>
						<P>
							We may disclose information if we believe in good
							faith that doing so is necessary to: comply with a
							legal obligation, subpoena, or court order;
							protect the rights, property, or safety of Flass,
							our users, or the public; investigate or prevent
							fraud, security, or technical issues; or enforce
							our Terms of Service.
						</P>

						<SubHeading>5.5 Business transfers</SubHeading>
						<P>
							If we are involved in a merger, acquisition,
							financing, or sale of assets, your information may
							be transferred as part of that transaction,
							subject to this Policy or a policy at least as
							protective of your information.
						</P>
						<P>
							We do not sell or rent your personal information
							to third parties for their own marketing purposes.
						</P>
					</Article>

					<Article
						id="data-retention"
						ref={(el) => (sectionRefs.current['data-retention'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>7</ArticleNum>Data Retention
						</ArticleHeading>
						<P>
							We retain your information for as long as your
							account is active or as needed to provide the
							Service. We may retain certain information for
							longer periods where required by law, to resolve
							disputes, to enforce our agreements, or where an
							Institution requires retention consistent with
							its own record-keeping obligations. You may
							request deletion of your account and associated
							content as described in Section 7 below, subject
							to the exceptions described there.
						</P>
					</Article>

					<Article
						id="your-rights"
						ref={(el) => (sectionRefs.current['your-rights'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>8</ArticleNum>Your Rights and Choices
						</ArticleHeading>
						<P>Depending on your location and applicable law, you may have the right to:</P>
						<List>
							<li>
								<strong>Access</strong> a copy of the personal
								information we hold about you;
							</li>
							<li><strong>Correct</strong> inaccurate or incomplete information;</li>
							<li>
								<strong>Delete</strong> your account and
								associated personal information, subject to
								legal, security, or contractual retention
								requirements (for example, content shared with
								other users in a group may persist in that
								group&rsquo;s history even after your account
								is deleted, shown as originating from a
								deactivated user);
							</li>
							<li>
								<strong>Object to or restrict</strong> certain
								processing, and <strong>withdraw consent</strong>{' '}
								for optional features such as location sharing
								or camera/microphone access at any time
								through your device settings;
							</li>
							<li><strong>Data portability</strong>, where applicable;</li>
							<li>
								<strong>Opt out</strong> of push and email
								notifications through your in-app settings or
								your device&rsquo;s notification settings.
							</li>
						</List>
						<P>
							To exercise these rights, contact us at
							ergilaze@gmail.com. If you access the Service
							through an Institution, we may direct certain
							requests to your Institution where it is the
							appropriate party to respond, or process them
							jointly with your Institution as required by our
							agreement with them.
						</P>
						<P>
							California residents may have additional rights
							under the California Consumer Privacy Act, as
							amended, including the right to know, delete, and
							correct personal information, and to
							non-discrimination for exercising these rights. We
							do not sell or share personal information as
							defined under that law.
						</P>
					</Article>

					<Article
						id="data-security"
						ref={(el) => (sectionRefs.current['data-security'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>9</ArticleNum>Data Security
						</ArticleHeading>
						<P>
							We implement technical and organizational measures
							designed to protect your information, including
							encryption in transit, hashed password storage,
							access controls, and secure cloud infrastructure.
							However, no method of transmission or storage is
							completely secure, and we cannot guarantee
							absolute security. You are responsible for keeping
							your account credentials confidential.
						</P>
					</Article>

					<Article
						id="childrens-privacy"
						ref={(el) => (sectionRefs.current['childrens-privacy'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>10</ArticleNum>Children&rsquo;s Privacy
						</ArticleHeading>
						<P>
							The Service is intended for use by individuals
							affiliated with a post-secondary educational
							Institution and is not directed to children under
							the age of 13 (or the applicable minimum age in
							your jurisdiction). We do not knowingly collect
							personal information from children under this
							age. If we become aware that we have inadvertently
							collected such information, we will take steps to
							delete it. If you believe a child has provided us
							with personal information, please contact us at
							ergilaze@gmail.com.
						</P>
					</Article>

					<Article
						id="international-transfers"
						ref={(el) =>
							(sectionRefs.current['international-transfers'] = el)
						}
					>
						<ArticleHeading>
							<ArticleNum>11</ArticleNum>International Data Transfers
						</ArticleHeading>
						<P>
							Your information may be transferred to, stored,
							and processed in countries other than the one in
							which you reside. Based on the infrastructure
							providers listed in Section&nbsp;5.3, this
							currently includes: the European Union (database
							hosting via MongoDB Atlas), the Netherlands (file
							storage via Wasabi Technologies), and the United
							States (Railway, Redis Cloud, Resend, Sentry, and
							Apple, Google, and Expo&rsquo;s push notification
							services, each processing information in their
							own jurisdictions). As the Service is operated by
							an individual developer based in Albania, account
							and administrative access to the underlying
							systems may also occur from Albania. This may
							include countries whose data protection laws
							differ from those of your jurisdiction. Where
							required, we rely on appropriate safeguards, such
							as Standard Contractual Clauses, to protect
							information transferred internationally.
						</P>
					</Article>

					<Article
						id="third-party-links"
						ref={(el) => (sectionRefs.current['third-party-links'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>12</ArticleNum>Third-Party Links and Institutional Systems
						</ArticleHeading>
						<P>
							The Service may contain links to third-party
							websites or integrate with your Institution&rsquo;s
							own systems (such as its learning management
							system for sign-in). This Policy does not apply
							to those third-party websites or systems, and we
							encourage you to review their respective privacy
							policies.
						</P>
					</Article>

					<Article
						id="changes"
						ref={(el) => (sectionRefs.current['changes'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>13</ArticleNum>Changes to This Policy
						</ArticleHeading>
						<P>
							We may update this Privacy Policy from time to
							time to reflect changes in our practices or for
							legal, operational, or regulatory reasons. We will
							post the updated Policy with a new &ldquo;Last
							updated&rdquo; date, and where changes are
							material, we will provide additional notice (such
							as an in-app notification or email) before the
							changes take effect.
						</P>
					</Article>

					<Article
						id="contact"
						ref={(el) => (sectionRefs.current['contact'] = el)}
					>
						<ArticleHeading>
							<ArticleNum>14</ArticleNum>Contact Us
						</ArticleHeading>
						<P>
							If you have questions or concerns about this
							Privacy Policy or our data practices, please
							contact us at:
						</P>
						<Address>
							Ergi Laze
							<br />
							Flass
							<br />
							ergilaze@gmail.com
						</Address>
						<P>
							If you are located in the EEA or UK and believe we
							have not adequately addressed your concerns, you
							have the right to lodge a complaint with your
							local data protection authority.
						</P>
					</Article>

					<BackToTop href="#top">↑ Back to top</BackToTop>
				</Main>
			</Layout>

			<Copyright />
		</Wrapper>
	);
};

export default Privacy;
